import { defineStore } from 'pinia'
import { auth, db, storage } from '../services/firebase/config'
import { clearSessionCaches } from '../utils/globalCacheManager'
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  updateProfile,
  GoogleAuthProvider,
  signInWithPopup,
  sendPasswordResetEmail,
  sendEmailVerification,
  updatePassword,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence
} from 'firebase/auth'
import {
  doc,
  setDoc,
  getDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  onSnapshot,
  orderBy,
  limit,
  startAfter
} from 'firebase/firestore'
import { ref as storageRef, deleteObject } from 'firebase/storage'
import { useNotify } from '@/composables/useNotify'
import MediaService from '@/services/media/MediaService'
// uuid no longer needed for avatar deterministic path
import { targetedCloneForMutation } from '@/utils/cloneHelpers'

export const useUserStore = defineStore('user', {
  state: () => ({
    user: null,
    authInitialized: false,
    authReady: false,
    loading: false,
    error: null,
    profile: null,
    userCache: {}, // Add cache to store fetched user profiles
    // Firestore unsubscribe handle for notifications listener
    notifUnsub: null,
    // Firestore unsubscribe handle for own profile listener
    profileUnsub: null,
    // Track last seen claims/version to decide on token refresh
    _lastClaimsVersion: null
  }),

  getters: {
    isAuthenticated: (state) => !!state.user,
    userEmail: (state) => state.user?.email || '',
    userName: (state) => state.user?.displayName || '',
    userAvatar: (state) => state.user?.photoURL || '',
    userId: (state) => state.user?.uid || '',
    userProfile: (state) => state.profile || {},
    isAdmin: (state) => state.profile?.roles?.admin || false,
    userPlan: (state) => state.profile?.plan || 'free'
  },

  actions: {
    async setUser(user) {
      // Ensure any previous listener is stopped before switching users
      try {
        this.stopNotificationsListener()
      } catch {
        /* ignore */
      }
      try {
        this.stopProfileListener()
      } catch {
        /* ignore */
      }
      this.user = user
      this.authInitialized = false
      // Reset authReady until boot finalizes token refresh
      this.authReady = false

      // If user is logged in, ensure they have a profile in Firestore
      if (user) {
        try {
          // console.log('Setting user in store, uid:', user.uid)

          // First try creating user document with retry logic for offline scenarios
          await this.ensureUserDocument(user)

          // Then fetch the user profile
          await this.fetchUserProfile()
          // Start listening for user notifications (to show toasts)
          try {
            this.startNotificationsListener()
          } catch {
            /* non-fatal */
          }
          // Start real-time updates for own profile (plan, roles, photoURL, etc.)
          try {
            this.startProfileListener()
          } catch {
            /* non-fatal */
          }

          // Ensure external provider avatars are migrated to our Storage for stability/CDN
          try {
            // run without blocking the UI; non-critical if it fails
            this.migrateAvatarToStorageIfExternal()
          } catch {
            /* ignore */
          }
        } catch (error) {
          console.error('Error in setUser method:', error)
          console.error('Full error object:', JSON.stringify(error))
        }
      } else {
        this.profile = null
        this._lastClaimsVersion = null
        this.authReady = false
      }
    },

    setAuthReady(ready) {
      this.authReady = !!ready
      this.authInitialized = true
    },

    async waitForAuthState({ requireUser = false, timeoutMs = 5000 } = {}) {
      const isSatisfied = () => {
        if (!this.authInitialized) return false
        if (!this.user?.uid) return !requireUser
        return !!this.authReady
      }

      if (isSatisfied()) return true
      if (this.authInitialized && !this.user?.uid) return !requireUser

      return await new Promise((resolve) => {
        let settled = false
        let unsubscribe = null
        let timer = null

        const finish = (value) => {
          if (settled) return
          settled = true
          try {
            if (typeof unsubscribe === 'function') unsubscribe()
          } catch {
            /* ignore cleanup errors */
          }
          if (timer) clearTimeout(timer)
          resolve(!!value)
        }

        const checkState = () => {
          if (isSatisfied()) {
            finish(true)
            return
          }
          if (this.authInitialized && requireUser && !this.user?.uid) {
            finish(false)
          }
        }

        unsubscribe = this.$subscribe(
          () => {
            checkState()
          },
          { detached: true }
        )

        timer = setTimeout(() => {
          finish(isSatisfied())
        }, timeoutMs)

        checkState()
      })
    },

    startNotificationsListener() {
      try {
        if (!this.user || this.notifUnsub) return
        const notify = useNotify()
        const ref = collection(db, 'users', this.user.uid, 'notifications')
        // Listen to unseen notifications, newest first
        const q = query(ref, where('seen', '==', false), orderBy('createdAt', 'desc'), limit(10))
        const unsub = onSnapshot(q, async (snap) => {
          for (const change of snap.docChanges()) {
            try {
              if (change.type === 'added') {
                const data = change.doc.data() || {}
                let msg = data.message || 'An update occurred'
                // Robust JSON extraction helper: handles plain JSON, embedded
                // JSON and double-stringified JSON (e.g. "{\"error\":{...}}" )
                const tryExtractJson = (text) => {
                  if (!text || typeof text !== 'string') return null
                  // 1) Try direct JSON.parse, and if that yields a string, parse again
                  try {
                    const p = JSON.parse(text)
                    if (p && typeof p === 'object') return p
                    if (typeof p === 'string') {
                      try {
                        const p2 = JSON.parse(p)
                        if (p2 && typeof p2 === 'object') return p2
                      } catch {
                        /* ignore */
                      }
                    }
                  } catch {
                    /* ignore */
                  }

                  // 2) Look for first {...} substring and try parsing it
                  try {
                    const m = text.match(/(\{[\s\S]*\})/)
                    if (m && m[1]) {
                      try {
                        const p3 = JSON.parse(m[1])
                        if (p3 && typeof p3 === 'object') return p3
                      } catch {
                        // Try unescaping common escapes like \" back to " then parse
                        try {
                          const un = m[1].replace(/\\"/g, '"').replace(/\\n/g, '')
                          const p4 = JSON.parse(un)
                          if (p4 && typeof p4 === 'object') return p4
                        } catch {
                          /* ignore */
                        }
                      }
                    }
                  } catch {
                    /* ignore */
                  }
                  return null
                }

                try {
                  const parsed = tryExtractJson(msg)
                  if (parsed) {
                    if (parsed.error && parsed.error.message) msg = String(parsed.error.message)
                    else if (parsed.message) msg = String(parsed.message)
                    else if (parsed.error && typeof parsed.error === 'string') msg = parsed.error
                  }
                } catch {
                  /* ignore and keep raw msg */
                }

                // If server included a rawError field, prefer that (it may itself be JSON)
                if ((!msg || msg === 'An update occurred') && data.rawError) {
                  try {
                    const re =
                      typeof data.rawError === 'string' ? data.rawError : String(data.rawError)
                    if (re.trim().startsWith('{')) {
                      const parsed2 = JSON.parse(re)
                      if (parsed2 && parsed2.error && parsed2.error.message)
                        msg = String(parsed2.error.message)
                      else if (parsed2.message) msg = String(parsed2.message)
                    } else if (re) {
                      msg = re
                    }
                  } catch {
                    /* ignore */
                  }
                }

                // Use friendly mapping for our ai notifications
                const opts = {}
                if (data.caption) opts.caption = String(data.caption)
                if (data.type === 'aiJobFailed') {
                  notify.negative(msg, opts)
                } else if (data.type === 'aiJobCompleted') {
                  notify.positive(msg, opts)
                } else {
                  notify({ message: msg, ...opts })
                }

                // Mark seen so we don't re-notify
                try {
                  await updateDoc(doc(db, 'users', this.user.uid, 'notifications', change.doc.id), {
                    seen: true
                  })
                } catch {
                  /* ignore */
                }
              }
            } catch {
              /* ignore per-change errors */
            }
          }
        })
        this.notifUnsub = unsub
      } catch (e) {
        // Non-fatal
        console.warn('Failed to start notifications listener', e?.message || e)
      }
    },

    stopNotificationsListener() {
      try {
        if (this.notifUnsub) {
          this.notifUnsub()
          this.notifUnsub = null
        }
      } catch {
        /* ignore */
      }
    },

    startProfileListener() {
      try {
        if (!this.user || this.profileUnsub) return
        const uid = this.user.uid
        const unsub = onSnapshot(doc(db, 'users', uid), async (snap) => {
          try {
            if (!snap.exists()) return
            const data = snap.data() || {}
            // Normalize defaults
            const newProfile = {
              ...data,
              plan: data.plan || 'free',
              planExpiration: data.planExpiration || null
            }
            this.profile = newProfile

            // Cache for offline use
            try {
              localStorage.setItem(
                'cachedUserProfile',
                JSON.stringify({ timestamp: Date.now(), data: this.profile })
              )
            } catch {
              /* ignore */
            }

            // Optional: refresh token if backend bumps claims version marker
            const claimsVersion = newProfile.tokenVersion || newProfile.claimsUpdatedAt || null
            if (claimsVersion && claimsVersion !== this._lastClaimsVersion) {
              try {
                await this.user.getIdToken(true)
              } catch {
                /* ignore */
              }
              this._lastClaimsVersion = claimsVersion
            }
          } catch {
            /* ignore per-snapshot */
          }
        })
        this.profileUnsub = unsub
      } catch (e) {
        // Non-fatal
        console.warn('Failed to start profile listener', e?.message || e)
      }
    },

    stopProfileListener() {
      try {
        if (this.profileUnsub) {
          this.profileUnsub()
          this.profileUnsub = null
        }
      } catch {
        /* ignore */
      }
    },

    // New method to handle creating user documents with retry logic
    async ensureUserDocument(user, retryCount = 0) {
      if (!user) return

      try {
        let tokenClaims = {}
        try {
          const tokenResult = await user.getIdTokenResult()
          tokenClaims = tokenResult?.claims || {}
        } catch {
          tokenClaims = {}
        }
        const hasAdminClaim =
          tokenClaims?.admin === true || tokenClaims?.roles?.admin === true

        // console.log('Checking if user document exists in Firestore')
        const userDoc = await getDoc(doc(db, 'users', user.uid))

        // console.log('User document exists:', userDoc.exists())

        if (!userDoc.exists()) {
          // Create profile for users that exist in Auth but not in Firestore
          const userProfile = {
            uid: user.uid,
            email: user.email,
            displayName: user.displayName || user.email?.split('@')[0] || 'User',
            photoURL: user.photoURL,
            createdAt: new Date().toISOString(),
            lastLogin: new Date().toISOString(),
            roles: {
              player: true, // Default role is player
              ...(hasAdminClaim ? { admin: true } : {})
            },
            plan: 'free' // Default plan
          }
          try {
            await setDoc(doc(db, 'users', user.uid), userProfile)
            return true
          } catch (setDocError) {
            console.error('Error creating user document:', setDocError)
            if (setDocError.code === 'unavailable' || setDocError.code === 'failed-precondition') {
              if (retryCount < 3) {
                return new Promise((resolve) => {
                  setTimeout(async () => {
                    const result = await this.ensureUserDocument(user, retryCount + 1)
                    resolve(result)
                  }, 2000)
                })
              }
            }
            throw setDocError
          }
        }

        // Keep Firestore roles aligned with auth claims without removing existing roles.
        if (hasAdminClaim && !userDoc.data()?.roles?.admin) {
          await updateDoc(doc(db, 'users', user.uid), { 'roles.admin': true })
        }
        return true
      } catch (error) {
        if (error.code === 'unavailable' && retryCount < 3) {
          // console.log(`Retry attempt ${retryCount + 1} after connection error`)
          return new Promise((resolve) => {
            setTimeout(async () => {
              const result = await this.ensureUserDocument(user, retryCount + 1)
              resolve(result)
            }, 2000) // Wait 2 seconds before retry
          })
        }
        throw error
      }
    },

    async register({ email, password, displayName }) {
      try {
        this.loading = true
        this.error = null

        // Create user with email and password
        const userCredential = await createUserWithEmailAndPassword(auth, email, password)

        // Update profile with display name
        await updateProfile(userCredential.user, { displayName })

        // Send email verification
        await sendEmailVerification(userCredential.user)

        // Create user profile in Firestore
        const userProfile = {
          uid: userCredential.user.uid,
          email,
          displayName,
          createdAt: new Date().toISOString(),
          lastLogin: new Date().toISOString(),
          roles: {
            player: true // Default role is player
          },
          plan: 'free' // Default plan
        }

        await setDoc(doc(db, 'users', userCredential.user.uid), userProfile)
        // Update local user state
        this.user = userCredential.user
        this.profile = userProfile

        return userCredential.user
      } catch (error) {
        this.error = error.message
        throw error
      } finally {
        this.loading = false
      }
    },

    /**
     * Admin: Update a user's plan
     * @param {string} userId
     * @param {string} planId
     */
    async updateUserPlan(userId, planId) {
      if (!userId || !planId) return
      try {
        await updateDoc(doc(db, 'users', userId), { plan: planId })
        // If updating self, update local profile
        if (this.user?.uid === userId) {
          this.profile = { ...this.profile, plan: planId }
        }
        // Update cache if present
        if (this.userCache[userId]) {
          this.userCache[userId].plan = planId
        }
      } catch (error) {
        console.error('Failed to update user plan:', error)
        throw error
      }
    },

    async login({ email, password, rememberMe }) {
      try {
        this.loading = true
        this.error = null

        // Set persistence based on remember me option
        await setPersistence(auth, rememberMe ? browserLocalPersistence : browserSessionPersistence)

        const userCredential = await signInWithEmailAndPassword(auth, email, password)
        this.user = userCredential.user

        // Check if user profile exists in Firestore
        const userDoc = await getDoc(doc(db, 'users', userCredential.user.uid))

        if (!userDoc.exists()) {
          // Create profile for users that exist in Auth but not in Firestore
          const userProfile = {
            uid: userCredential.user.uid,
            email: userCredential.user.email,
            displayName: userCredential.user.displayName || email.split('@')[0],
            photoURL: userCredential.user.photoURL,
            createdAt: new Date().toISOString(),
            lastLogin: new Date().toISOString(),
            roles: {
              player: true // Default role is player
            }
          }

          await setDoc(doc(db, 'users', userCredential.user.uid), userProfile)
        } else {
          // Update lastLogin timestamp for existing users
          await updateDoc(doc(db, 'users', userCredential.user.uid), {
            lastLogin: new Date().toISOString()
          })
        }

        await this.fetchUserProfile()

        return userCredential.user
      } catch (error) {
        this.error = error.message
        throw error
      } finally {
        this.loading = false
      }
    },

    async loginWithGoogle() {
      try {
        this.loading = true
        this.error = null

        // console.log('Starting Google login process')
        const provider = new GoogleAuthProvider()

        try {
          // console.log('Attempting Google sign-in popup')
          const userCredential = await signInWithPopup(auth, provider)
          // console.log('Google sign-in successful, user:', userCredential.user.uid)
          this.user = userCredential.user

          // Ensure user profile exists in Firestore. This helper already retries
          // on transient emulator/network readiness issues.
          try {
            await this.ensureUserDocument(userCredential.user)
          } catch (profileEnsureError) {
            // Non-fatal for auth success: we can still continue with in-memory auth
            // and later profile listeners/retries will converge.
            console.warn(
              'Google login: ensureUserDocument failed (continuing auth session):',
              profileEnsureError
            )
          }

          // Best-effort lastLogin update; ignore transient offline errors.
          try {
            await updateDoc(doc(db, 'users', userCredential.user.uid), {
              lastLogin: new Date().toISOString()
            })
          } catch (updateError) {
            console.warn('Google login: lastLogin update skipped:', updateError?.code || updateError)
          }

          // console.log('Fetching Google user profile')
          await this.fetchUserProfile()

          return userCredential.user
        } catch (authError) {
          console.error('Google authentication error:', authError)
          console.error('Auth error details:', JSON.stringify(authError))
          throw authError
        }
      } catch (error) {
        console.error('Overall Google login error:', error)
        this.error = error.message
        throw error
      } finally {
        this.loading = false
      }
    },

    /**
     * If the current user's photoURL points to an external provider (e.g., Google lh3),
     * copy it into our Firebase Storage and switch profile to the stable CDN URL.
     */
    async migrateAvatarToStorageIfExternal() {
      try {
        if (!this.user) return
        const uid = this.user.uid
        // Prefer Auth photoURL then profile photoURL
        const src = this.user.photoURL || this.profile?.photoURL
        if (!src || typeof src !== 'string') return

        const isOurStorage =
          /firebasestorage\.googleapis\.com|storage\.googleapis\.com|\/(users%2F|users\/)/i.test(
            src
          )
        if (isOurStorage) return

        const isGoogleLh3 = /^https?:\/\/lh\d+\.googleusercontent\.com\//i.test(src)
        const isExternal = isGoogleLh3 || /^https?:\/\//i.test(src)
        if (!isExternal) return

        // Avoid parallel migrations within the same session
        if (this._avatarMigrationInProgress) return
        this._avatarMigrationInProgress = true

        // Fetch the external image
        const response = await fetch(src, { mode: 'cors', referrerPolicy: 'no-referrer' })
        if (!response.ok) throw new Error('Failed to fetch provider avatar')
        const blob = await response.blob()
        // Force JPEG; our ImageEditor/export path outputs JPEG
        const file = new File([blob], 'provider-avatar.jpg', { type: 'image/jpeg' })

        // Deterministic .jpg path in profile folder (overwrites existing)
        const folderPath = `users/${uid}/profile`
        const storagePath = `${folderPath}/avatar.jpg`

        // Best-effort delete previous by URL/path before upload (no listing needed)
        try {
          const prevUrl = this.user?.photoURL || this.profile?.photoURL || null
          if (prevUrl) await MediaService.deleteByUrl(prevUrl)
        } catch {
          /* ignore */
        }

        const res = await MediaService.uploadManaged({
          storagePath,
          file,
          userId: uid,
          resourceType: 'user-avatar',
          makePublic: true
        })
        const url = res?.url || null
        if (!url) throw new Error('Upload returned no URL')

        // Update both Auth and Firestore to use our hosted URL
        try {
          await updateProfile(this.user, { photoURL: url })
        } catch {
          /* ignore minor */
        }
        try {
          await updateDoc(doc(db, 'users', uid), {
            photoURL: url,
            avatarStoragePath: storagePath,
            avatarUpdatedAt: new Date().toISOString()
          })
        } catch {
          /* ignore minor */
        }

        // Legacy cleanup: prior no-extension file and previous avatar folder path
        try {
          await deleteObject(storageRef(storage, `users/${uid}/profile/avatar`))
        } catch {
          /* ignore */
        }
        try {
          await deleteObject(storageRef(storage, `users/${uid}/avatar/avatar.jpg`))
        } catch {
          /* ignore */
        }

        // Refresh local profile cache
        try {
          await this.fetchUserProfile()
        } catch {
          /* ignore */
        }
      } catch (e) {
        // Non-fatal
        console.warn('Avatar migration skipped/failed:', e?.message || e)
      } finally {
        this._avatarMigrationInProgress = false
      }
    },

    async fetchUserProfile() {
      if (!this.user) return null

      try {
        this.loading = true
        // console.log('Fetching user profile for uid:', this.user.uid)

        try {
          const userDoc = await getDoc(doc(db, 'users', this.user.uid))
          // console.log('Fetch user profile result - exists:', userDoc.exists())

          if (userDoc.exists()) {
            const data = userDoc.data() || {}
            // Always ensure plan and planExpiration are present
            this.profile = {
              ...data,
              plan: data.plan || 'free',
              planExpiration: data.planExpiration || null
            }
            // Cache the profile in localStorage for offline access
            localStorage.setItem(
              'cachedUserProfile',
              JSON.stringify({
                timestamp: Date.now(),
                data: this.profile
              })
            )
            // console.log('User profile data loaded and cached:', this.profile)
          } else {
            console.warn(
              'User document does not exist in Firestore despite previous creation attempt'
            )
          }
        } catch (error) {
          if (error.code === 'unavailable' || error.message.includes('offline')) {
            // console.log('Offline mode - attempting to load cached profile')
            const cachedProfile = localStorage.getItem('cachedUserProfile')
            if (cachedProfile) {
              const { data, timestamp } = JSON.parse(cachedProfile)
              // Only use cache if it's less than 24 hours old
              if (Date.now() - timestamp < 24 * 60 * 60 * 1000) {
                this.profile = {
                  ...data,
                  plan: data.plan || 'free',
                  planExpiration: data.planExpiration || null
                }
                // console.log('Using cached profile data:', this.profile)
              }
            }
          } else {
            throw error
          }
        }

        return this.profile
      } catch (error) {
        console.error('Error fetching user profile:', error)
        console.error('Error details:', JSON.stringify(error))
        this.error = error.message
      } finally {
        this.loading = false
      }
    },

    async updateProfile(profileData) {
      if (!this.user) return

      try {
        this.loading = true
        this.error = null

        // Update profile in Firestore
        await updateDoc(doc(db, 'users', this.user.uid), profileData)

        // If there's a displayName, update it in Firebase Auth too
        if (profileData.displayName) {
          await updateProfile(this.user, {
            displayName: profileData.displayName
          })
        }

        // Re-fetch the updated profile
        await this.fetchUserProfile()

        return this.profile
      } catch (error) {
        this.error = error.message
        throw error
      } finally {
        this.loading = false
      }
    },

    async sendVerificationEmail() {
      if (!this.user) return

      try {
        this.loading = true
        this.error = null

        await sendEmailVerification(this.user)
      } catch (error) {
        this.error = error.message
        throw error
      } finally {
        this.loading = false
      }
    },

    async uploadAvatar(file) {
      if (!this.user || !file) return

      try {
        this.loading = true
        this.error = null

        const uid = this.user.uid
        // Deterministic path in profile folder with .jpg extension (ImageEditor conversion ensures JPEG)
        const folderPath = `users/${uid}/profile`
        const storagePath = `${folderPath}/avatar.jpg`

        // Delete previous avatar by exact URL/path before upload
        try {
          const prevUrl = this.user?.photoURL || this.profile?.photoURL || null
          if (prevUrl) await MediaService.deleteByUrl(prevUrl)
        } catch {
          /* ignore */
        }

        const res = await MediaService.uploadManaged({
          storagePath,
          file,
          userId: uid,
          resourceType: 'user-avatar',
          makePublic: true
        })
        const url = res?.url || null

        // Update user profile with photo URL
        await updateProfile(this.user, { photoURL: url })
        await updateDoc(doc(db, 'users', uid), {
          photoURL: url,
          avatarStoragePath: storagePath,
          avatarUpdatedAt: new Date().toISOString()
        })

        // Best-effort cleanup of legacy paths if they exist
        try {
          await deleteObject(storageRef(storage, `users/${uid}/profile/avatar`))
        } catch {
          /* ignore */
        }
        try {
          await deleteObject(storageRef(storage, `users/${uid}/avatar/avatar.jpg`))
        } catch {
          /* ignore */
        }

        // Re-fetch the updated profile
        await this.fetchUserProfile()

        return url
      } catch (error) {
        console.error('Avatar upload failed:', error)
        this.error = error.message
        throw error
      } finally {
        this.loading = false
      }
    },

    async deleteAvatar() {
      if (!this.user) return

      try {
        this.loading = true
        this.error = null

        // Check if the user needs to refresh their token before proceeding
        // This ensures custom claims are up-to-date before storage operations
        if (this.profile?.roles?.admin) {
          try {
            // console.log('Admin user detected, refreshing token before storage operations')
            await this.user.getIdToken(true) // Force token refresh
          } catch {
            // console.warn('Token refresh failed, continuing with operation')
          }
        }

        const avatarRef = storageRef(storage, `users/${this.user.uid}/profile/avatar`)

        // Delete from storage
        await deleteObject(avatarRef)

        // Update user profile
        await updateProfile(this.user, {
          photoURL: null
        })

        // Update Firestore
        await updateDoc(doc(db, 'users', this.user.uid), {
          photoURL: null,
          avatarUpdatedAt: new Date().toISOString()
        })

        // Re-fetch profile
        await this.fetchUserProfile()
      } catch (error) {
        this.error = error.message
        throw error
      } finally {
        this.loading = false
      }
    },

    async importGoogleAvatar() {
      if (!this.user?.photoURL || !this.user?.providerData?.[0]?.photoURL) return

      try {
        this.loading = true
        this.error = null

        // Fetch the Google avatar
        const response = await fetch(this.user.providerData[0].photoURL)
        const blob = await response.blob()

        // Create a File object from the blob
        const file = new File([blob], 'google-avatar.jpg', { type: 'image/jpeg' })

        // Upload using our normal avatar upload method
        return await this.uploadAvatar(file)
      } catch (error) {
        this.error = error.message
        throw error
      } finally {
        this.loading = false
      }
    },

    /**
     * Get a user by their user ID
     * @param {string} userId - The user ID to fetch
     * @returns {Promise<Object|null>} - The user profile or null if not found
     */
    async getUserById(userId) {
      if (!userId) return null

      // Return from cache if available to prevent redundant fetches
      if (this.userCache[userId]) {
        return this.userCache[userId]
      }

      try {
        // Fetch the user document from Firestore
        const userDoc = await getDoc(doc(db, 'users', userId))

        if (userDoc.exists()) {
          // Add to cache using targeted per-item clone to avoid leaking DB refs
          const data = userDoc.data()
          this.userCache[userId] = targetedCloneForMutation(data)
          return this.userCache[userId]
        }

        return null
      } catch (error) {
        console.error(`Error fetching user with ID ${userId}:`, error)
        return null
      }
    },

    /**
     * Get a user by their email address
     * @param {string} email - The email address to search for
     * @returns {Promise<Object|null>} - The user profile or null if not found
     */
    async getUserByEmail(email) {
      if (!email) return null

      try {
        // Check if any cached user has this email
        const cachedUserEntry = Object.entries(this.userCache).find((entry) => {
          const userData = entry[1]
          return userData.email === email
        })

        if (cachedUserEntry) {
          return cachedUserEntry[1]
        }

        // Query Firestore for user with matching email using where clause
        const usersRef = collection(db, 'users')
        const q = query(usersRef, where('email', '==', email))
        const querySnapshot = await getDocs(q)

        if (!querySnapshot.empty) {
          // Get the first matching user
          const userData = querySnapshot.docs[0].data()

          // Cache the user data using targeted clone
          this.userCache[userData.uid] = targetedCloneForMutation(userData)

          return this.userCache[userData.uid]
        }

        return null
      } catch (error) {
        console.error(`Error fetching user with email ${email}:`, error)
        return null
      }
    },

    /**
     * Clear user cache for specific user ID or all users
     * @param {string|null} userId - The user ID to clear from cache, or null to clear all
     */
    clearUserCache(userId = null) {
      if (userId) {
        // Clear specific user from cache
        if (this.userCache[userId]) {
          delete this.userCache[userId]
          console.log(`Cleared user cache for user ID: ${userId}`)
        }
      } else {
        // Clear entire user cache
        this.userCache = {}
        console.log('Cleared entire user cache')
      }
    },

    async resetPassword(email) {
      try {
        this.loading = true
        this.error = null

        await sendPasswordResetEmail(auth, email)
      } catch (error) {
        this.error = error.message
        throw error
      } finally {
        this.loading = false
      }
    },

    async updateUserPassword(newPassword) {
      if (!this.user) return

      try {
        this.loading = true
        this.error = null

        await updatePassword(this.user, newPassword)
      } catch (error) {
        this.error = error.message
        throw error
      } finally {
        this.loading = false
      }
    },

    async logout() {
      try {
        this.loading = true
        this.error = null

        // First clear our user data and profile
        // Stop notifications listener before clearing user
        try {
          this.stopNotificationsListener()
        } catch {
          /* ignore */
        }
        try {
          this.stopProfileListener()
        } catch {
          /* ignore */
        }
        this.user = null
        this.profile = null
        this._lastClaimsVersion = null

        // Clear user cache
        this.clearUserCache()

        // Clear any cached profile
        localStorage.removeItem('cachedUserProfile')

        // Clear session-related caches
        await clearSessionCaches()

        // Then sign out from Firebase
        await signOut(auth)

        return true
      } catch (error) {
        this.error = error.message
        throw error
      } finally {
        this.loading = false
      }
    },

    async checkUserIsDM() {
      if (!this.profile) await this.fetchUserProfile()
      return this.profile?.roles?.dm || false
    },

    async checkUserIsAdmin() {
      if (!this.profile) await this.fetchUserProfile()
      return this.profile?.roles?.admin || false
    },

    async upgradeToDM() {
      if (!this.user) return

      try {
        this.loading = true
        this.error = null

        // Update user role in Firestore
        await updateDoc(doc(db, 'users', this.user.uid), {
          'roles.dm': true
        })

        // Re-fetch the updated profile
        await this.fetchUserProfile()

        return this.profile
      } catch (error) {
        this.error = error.message
        throw error
      } finally {
        this.loading = false
      }
    },

    async syncEmailVerificationStatus() {
      if (!this.user) return false

      try {
        this.loading = true

        // Get the current verification status
        const emailVerified = this.user.emailVerified

        // Update the Firestore document
        await updateDoc(doc(db, 'users', this.user.uid), {
          emailVerified: emailVerified
        })

        // Re-fetch profile to ensure it's up-to-date
        await this.fetchUserProfile()

        return true
      } catch (error) {
        console.error('Error syncing verification status:', error)
        this.error = error.message
        return false
      } finally {
        this.loading = false
      }
    },

    /**
     * Get only public profile information for a user
     * Optimized for public campaign displays to work without authentication
     * @param {string} userId - The user ID to fetch public data for
     * @returns {Promise<Object|null>} - Limited public user data or null
     */
    async getUserPublicProfile(userId) {
      if (!userId) return null

      try {
        // Return from cache if available
        if (this.userCache[userId]) {
          // Return only the public fields from cache
          const { displayName, photoURL, uid } = this.userCache[userId]
          return { displayName, photoURL, uid }
        }

        // Optimize to avoid unnecessary fetches
        // Check localStorage for recently fetched profiles to reduce API calls
        const cacheKey = `user_profile_${userId}`
        const cachedProfile = localStorage.getItem(cacheKey)

        if (cachedProfile) {
          try {
            const { data, timestamp } = JSON.parse(cachedProfile)
            // Use cache if it's less than 1 hour old
            if (Date.now() - timestamp < 60 * 60 * 1000) {
              // Still store in memory cache for future use
              if (!this.userCache[userId]) {
                this.userCache[userId] = data
              }
              return {
                displayName: data.displayName,
                photoURL: data.photoURL,
                uid: data.uid
              }
            }
          } catch {
            // If parsing fails, proceed with fetch
            localStorage.removeItem(cacheKey)
          }
        }

        // Fetch the user document from Firestore with minimal fields
        const userDoc = await getDoc(doc(db, 'users', userId))

        if (userDoc.exists()) {
          const userData = userDoc.data()

          // Extract only public fields
          const publicProfile = {
            displayName: userData.displayName || 'Unknown User',
            photoURL: userData.photoURL,
            uid: userData.uid
          }

          // Cache the complete user data for future use (clone to avoid leaked refs)
          this.userCache[userId] = targetedCloneForMutation(userData)

          // Also cache in localStorage for persistence between sessions
          localStorage.setItem(
            cacheKey,
            JSON.stringify({
              timestamp: Date.now(),
              data: publicProfile
            })
          )

          return publicProfile
        }

        return null
      } catch (error) {
        console.error(`Error fetching public profile for user ID ${userId}:`, error)

        // For network/permission errors, try to get from localStorage as fallback
        try {
          const cacheKey = `user_profile_${userId}`
          const cachedProfile = localStorage.getItem(cacheKey)
          if (cachedProfile) {
            const { data } = JSON.parse(cachedProfile)
            return {
              displayName: data.displayName,
              photoURL: data.photoURL,
              uid: data.uid
            }
          }
        } catch {
          // Ignore errors in fallback
        }

        // Return a minimal profile to avoid UI errors
        return {
          displayName: 'Unknown User',
          photoURL: null,
          uid: userId
        }
      }
    },

    /**
     * Fetch paginated users for admin panel
     * @param {Object} options - Pagination options
     * @returns {Promise<Object>} { users, total, hasMore }
     */
    async fetchPaginatedUsers({
      pageSize = 25,
      sortBy = 'createdAt',
      descending = true,
      lastDoc = null
    }) {
      try {
        const usersCollection = collection(db, 'users')
        const orderByField = sortBy || 'createdAt'
        const orderDirection = descending ? 'desc' : 'asc'

        // Build query with ordering and limit
        let q
        if (lastDoc) {
          // If we have a lastDoc (for pagination), start after it
          q = query(
            usersCollection,
            orderBy(orderByField, orderDirection),
            startAfter(lastDoc),
            limit(pageSize + 1)
          )
        } else {
          // First page - no cursor needed
          q = query(
            usersCollection,
            orderBy(orderByField, orderDirection),
            limit(pageSize + 1) // Fetch one extra to check if there are more
          )
        }

        const snapshot = await getDocs(q)

        // Check if there are more results
        const hasMore = snapshot.docs.length > pageSize

        // Get only the requested page size
        const docs = hasMore ? snapshot.docs.slice(0, pageSize) : snapshot.docs

        const users = docs.map((doc) => {
          const data = doc.data()
          return {
            ...data,
            id: doc.id,
            plan: data.plan || 'free'
          }
        })

        // Get total count using a separate optimized query
        // We'll count documents in batches to avoid memory issues
        const countSnapshot = await getDocs(query(usersCollection))
        const total = countSnapshot.size

        // For pagination cursor, we need the actual DocumentSnapshot
        // Store it in a way that won't be cached by Firestore's persistence
        const lastDocSnapshot = docs.length > 0 ? docs[docs.length - 1] : null

        return {
          users,
          total,
          hasMore,
          lastDoc: lastDocSnapshot // Return the DocumentSnapshot directly
        }
      } catch (error) {
        console.error('Error fetching paginated users:', error)
        throw error
      }
    },

    /**
     * Search users by display name, email, or username
     * @param {string} searchQuery - The search query
     * @returns {Promise<Array>} Array of matching users
     */
    async searchUsers(searchQuery) {
      try {
        if (!searchQuery?.trim()) {
          return []
        }

        const usersCollection = collection(db, 'users')
        const normalizedQuery = searchQuery.toLowerCase().trim()

        // Since Firestore doesn't support case-insensitive or full-text search,
        // we'll fetch a reasonable set of users and filter client-side
        // This approach works better for admin searches which are infrequent

        // Fetch recent users (ordered by createdAt) - most likely to be searched
        const recentUsersQuery = query(usersCollection, orderBy('createdAt', 'desc'), limit(100))

        // Fetch users ordered by email (for email-based searches)
        const emailBasedQuery = query(usersCollection, orderBy('email', 'asc'), limit(100))

        // Execute queries in parallel
        const [recentSnapshot, emailSnapshot] = await Promise.all([
          getDocs(recentUsersQuery),
          getDocs(emailBasedQuery)
        ])

        // Combine and deduplicate results
        const userMap = new Map()

        const processSnapshot = (snapshot) => {
          snapshot.docs.forEach((doc) => {
            const data = doc.data()

            // Check if this user matches our search query (case-insensitive)
            const displayName = (data.displayName || '').toLowerCase()
            const email = (data.email || '').toLowerCase()
            const username = (data.username || '').toLowerCase()
            const uid = (data.uid || '').toLowerCase()

            if (
              displayName.includes(normalizedQuery) ||
              email.includes(normalizedQuery) ||
              username.includes(normalizedQuery) ||
              uid.includes(normalizedQuery)
            ) {
              userMap.set(doc.id, {
                ...data,
                id: doc.id,
                plan: data.plan || 'free'
              })
            }
          })
        }

        processSnapshot(recentSnapshot)
        processSnapshot(emailSnapshot)

        const results = Array.from(userMap.values())

        // Sort results by relevance (exact matches first, then partial matches)
        results.sort((a, b) => {
          const aDisplayName = (a.displayName || '').toLowerCase()
          const bDisplayName = (b.displayName || '').toLowerCase()
          const aEmail = (a.email || '').toLowerCase()
          const bEmail = (b.email || '').toLowerCase()

          // Exact match priority
          const aExact = aDisplayName === normalizedQuery || aEmail === normalizedQuery
          const bExact = bDisplayName === normalizedQuery || bEmail === normalizedQuery

          if (aExact && !bExact) return -1
          if (!aExact && bExact) return 1

          // Starts with priority
          const aStarts =
            aDisplayName.startsWith(normalizedQuery) || aEmail.startsWith(normalizedQuery)
          const bStarts =
            bDisplayName.startsWith(normalizedQuery) || bEmail.startsWith(normalizedQuery)

          if (aStarts && !bStarts) return -1
          if (!aStarts && bStarts) return 1

          return 0
        })

        return results
      } catch (error) {
        console.error('Error searching users:', error)
        throw error
      }
    }
  }
})
